import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  AlertTitle,
  Stack,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useRecordPaymentMutation } from '../../Service/reservationPaymentsApi';
import { RecordPaymentRequest } from '../../Types/reservationPaymentTypes';
import {
  getCashDocumentsErrorCode,
  getCashDocumentsErrorMessage,
} from '../../../../Utils/cashDocumentsErrorMapping';
import { CashDocumentsRoutes } from '../../../../Router/path';

interface AddPaymentModalProps {
  open: boolean;
  onClose: () => void;
  reservationId: number;
  remainingBalance: number;
  onSaved?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface PaymentFormData {
  amount: number;
  paymentDate: Dayjs | null;
  paymentMethod: string;
  note?: string | null;
}

const paymentMethods = [
  { value: 'Karta', label: 'Karta płatnicza' },
  { value: 'Gotówka', label: 'Gotówka (KP)' },
  { value: 'Blik', label: 'BLIK' },
  { value: 'PayU', label: 'PayU' },
  { value: 'Inne', label: 'Inna metoda' },
];

const schema = yup.object({
  amount: yup
    .number()
    .required('Kwota jest wymagana')
    .positive('Kwota musi być większa od 0')
    .max(1000000, 'Kwota nie może przekraczać 1,000,000 PLN'),
  paymentDate: yup.mixed<Dayjs>().nullable(),
  paymentMethod: yup
    .string()
    .required('Metoda płatności jest wymagana')
    .max(50, 'Metoda płatności może mieć maksymalnie 50 znaków'),
  note: yup.string().max(500, 'Notatka może mieć maksymalnie 500 znaków').nullable().notRequired(),
});

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  open,
  onClose,
  reservationId,
  remainingBalance,
  onSaved,
}) => {
  const navigate = useNavigate();
  const [recordPayment, { isLoading, error }] = useRecordPaymentMutation();
  const [submitErrorMessage, setSubmitErrorMessage] = React.useState<string | null>(null);
  const [showCashReportGuardCta, setShowCashReportGuardCta] = React.useState(false);
  const submitFallbackMessage = 'Nie udało się zapisać płatności. Sprawdź dane i spróbuj ponownie.';
  const cashReportGuardErrorCode = 'CASH_DOCUMENT_MISSING_OPEN_REPORT_CYCLE';

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      amount: 0,
      paymentDate: dayjs(),
      paymentMethod: 'Gotówka',
      note: undefined,
    },
  });

  // Reset form with current remainingBalance when modal opens
  React.useEffect(() => {
    if (open) {
      reset({
        amount: remainingBalance,
        paymentDate: dayjs(),
        paymentMethod: 'Gotówka',
        note: undefined,
      });
    }
  }, [open, remainingBalance, reset]);

  // Watch payment method to show auto-KP info for cash payments
  const watchPaymentMethod = useWatch({
    control,
    name: 'paymentMethod',
  });

  // Detect cash payment (case-insensitive)
  const isCashPayment = watchPaymentMethod?.toLowerCase().includes('gotówka') || false;

  const onSubmit = async (data: any) => {
    setSubmitErrorMessage(null);
    setShowCashReportGuardCta(false);
    const payload: RecordPaymentRequest = {
      amount: data.amount,
      paymentDate: data.paymentDate ? data.paymentDate.format('YYYY-MM-DD') : undefined,
      paymentMethod: data.paymentMethod,
      note: data.note || undefined,
    };

    try {
      await recordPayment({ reservationId, data: payload }).unwrap();
      reset();
      setShowCashReportGuardCta(false);
      onClose();
      onSaved?.();
    } catch (err) {
      const errorCode = getCashDocumentsErrorCode(err);
      setShowCashReportGuardCta(errorCode === cashReportGuardErrorCode);
      setSubmitErrorMessage(getCashDocumentsErrorMessage(err, submitFallbackMessage));
      console.error('Failed to record payment:', err);
    }
  };

  const handleClose = () => {
    setSubmitErrorMessage(null);
    setShowCashReportGuardCta(false);
    reset();
    onClose();
  };

  const handleOpenCashReports = () => {
    handleClose();
    navigate(CashDocumentsRoutes.root);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Dodaj płatność</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Stack spacing={2}>
              {/* Error Alert */}
              {(submitErrorMessage || error) && (
                <Alert severity="error">
                  {submitErrorMessage || getCashDocumentsErrorMessage(error, submitFallbackMessage)}
                  {showCashReportGuardCta && (
                    <Button
                      variant="text"
                      size="small"
                      sx={{ mt: 1, display: 'inline-flex' }}
                      onClick={handleOpenCashReports}
                    >
                      Przejdź do raportów kasowych
                    </Button>
                  )}
                </Alert>
              )}

              {/* Remaining Balance Hint */}
              {remainingBalance > 0 && (
                <Alert severity="info">
                  Pozostało do zapłaty: <strong>{remainingBalance.toFixed(2)} PLN</strong>
                </Alert>
              )}

              {/* Auto-KP Info for Cash Payments */}
              {isCashPayment && (
                <Alert severity="info" icon={<ReceiptIcon />}>
                  <AlertTitle>Automatyczne wystawienie dokumentu KP</AlertTitle>
                  Dla płatności gotówkowych system automatycznie wystawi dokument KP (kasa przyjęła)
                  w celu zachowania prawidłowej ewidencji kasowej. Będziesz mógł edytować dane
                  dokumentu w sekcji "Dokumenty kasowe (KP/KW)" poniżej.
                </Alert>
              )}

              {/* Amount */}
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Kwota"
                    type="number"
                    fullWidth
                    required
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                    InputProps={{
                      endAdornment: 'PLN',
                    }}
                  />
                )}
              />

              {/* Payment Date */}
              <Controller
                name="paymentDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label="Data płatności"
                    format="DD.MM.YYYY"
                    maxDate={dayjs()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.paymentDate,
                        helperText: errors.paymentDate?.message,
                      },
                    }}
                  />
                )}
              />

              {/* Payment Method */}
              <Controller
                name="paymentMethod"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Metoda płatności"
                    select
                    fullWidth
                    required
                    error={!!errors.paymentMethod}
                    helperText={errors.paymentMethod?.message}
                  >
                    {paymentMethods.map((method) => (
                      <MenuItem key={method.value} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              {/* Note */}
              <Controller
                name="note"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notatka"
                    multiline
                    rows={3}
                    fullWidth
                    error={!!errors.note}
                    helperText={errors.note?.message}
                    placeholder="Opcjonalna notatka do płatności..."
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isLoading}>
              Anuluj
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {isLoading ? 'Zapisywanie...' : 'Zapisz płatność'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddPaymentModal;
